import { analytics, performance } from "../../../firebase.ts";
import { trace, PerformanceTrace } from "firebase/performance";
import { logEvent, setUserProperties } from "firebase/analytics";
import template from "./sw-dictionary.mjs";
import Strings from "../../../models/Strings.json";
import { LANGUAGE } from "../../global.ts";
import { Dictionary, Word } from "../../../models/Dictionary.ts";
import Words from "../../../models/Dictionary.json";
import { HeartBank } from "../../../managers/HeartBank.ts";

import { render } from "lit-html";
import { Modal } from "flowbite";
import type { ModalOptions, ModalInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";
import { SwReader } from "../sw-reader/sw-reader.ts";

const strings: any = Strings;
const words: Word[] = Words;

export class SwDictionary extends HTMLElement {
    private heartbank = new HeartBank();
    private componentTrace: PerformanceTrace;
    private dict: HTMLElement;
    private modal: ModalInterface;

    private definition: HTMLElement;
    private form: HTMLFormElement;
    private word: HTMLInputElement;
    private trash: HTMLButtonElement;
    private speak: HTMLButtonElement;
    private back: HTMLButtonElement;

    private SwReader: SwReader | null = null;

    constructor() {
        super();
        this.componentTrace = trace(performance, "component");
        this.componentTrace.putAttribute("view", "sw-dictionary");
        this.componentTrace.start();
        render(template(null), this);

        this.dict = this.querySelector("#sw-dict")!;
        this.definition = this.querySelector("#sw-dict-definition")!;
        this.form = this.querySelector("#sw-dict-form")!;
        this.word = this.querySelector("#sw-dict-word")!;
        this.trash = this.querySelector("#sw-dict-trash")!;
        this.speak = this.querySelector("#sw-dict-speak")!;
        this.back = this.querySelector("#sw-dict-back")!;

        const modalOptions: ModalOptions = {
            placement: "center",
            backdrop: "dynamic",
            backdropClasses:
                "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40", // need 40!
            closable: true,
            onHide: () => {
                this.SwReader?.show();
                //window.location.hash = "#";
            },
            onShow: () => {
                this.dict.style.zIndex = "70";
                this.word.focus();
                //console.log("modal is shown");
            },
            onToggle: () => {
                //console.log("modal has been toggled");
            }
        };

        // instance options object
        const instanceOptions: InstanceOptions = {
            id: "sw-dict",
            override: true
        };

        this.modal = new Modal(this.dict, modalOptions, instanceOptions);
    }

    connectedCallback() {
        this.form.addEventListener("submit", (event) => {
            event.preventDefault();
            this.renderWord(this.word.value);
        });

        this.word.addEventListener("keyup", (event: KeyboardEvent) => {
            if (event.key !== "Enter") {
                const results = words.filter((entry) =>
                    entry.word.includes(this.word.value)
                );

                if (this.word.value === "") {
                    this.definition.textContent = strings.search2[LANGUAGE];
                } else if (results.length === 0) {
                    this.definition.textContent = strings.search3[LANGUAGE];
                } else {
                    const ol = document.createElement("ol");
                    results.slice(0, 10).forEach((result) => {
                        const li = document.createElement("li");
                        li.textContent = "📖 " + result.word;
                        li.onclick = () => this.renderWord(result.word);
                        ol.appendChild(li);
                    });
                    this.definition.textContent = "";
                    this.definition.appendChild(ol);
                }
            }
        });

        this.trash.addEventListener("click", (event) => {
            this.definition.textContent = strings.search2[LANGUAGE];
            this.word.value = "";
            this.word.focus();
        });

        this.speak.addEventListener("click", (event) => {
            const baseUrl = "https://translate.google.com/";
            const params = new URLSearchParams({
                sl: strings.language[LANGUAGE],
                tl: "en",
                text: this.word.value,
                op: "translate"
            });
            // const params = new URLSearchParams({
            //     ie: "UTF-8",
            //     tl: "km",
            //     q: this.word.value,
            //     client: "tw-ob"
            // });
            window.open(`${baseUrl}?${params.toString()}`, "_blank");
        });

        this.back.addEventListener("click", (event) => {
            this.definition.style.opacity = "0.2";
            const history = JSON.parse(localStorage.getItem("history")!);

            if (history.length === 1) {
                this.definition.innerHTML = history[0].definition;
                this.word.value = history[0].word;
            } else {
                history.pop();
                const entry: Dictionary = history[history.length - 1];
                this.definition.innerHTML = entry.definition;
                this.word.value = entry.word;
                localStorage.setItem("history", JSON.stringify(history));
            }

            this.addListeners();
            this.back.disabled = history.length == 1;
            this.definition.style.opacity = "1";

            if (history.length === 1) {
                this.back.classList.add("hidden");
            } else {
                this.back.classList.remove("hidden");
            }
        });

        this.componentTrace.incrementMetric("count", 1);
        this.componentTrace.stop();
    }

    show(modal: SwReader | null = null) {
        this.SwReader = modal;
        this.modal.show();
    }

    hide() {
        this.modal.hide();
    }

    async renderId(id: string) {
        this.definition.style.opacity = "0.2";
        const dictionary = await fetch(
            `https://raw.githubusercontent.com/SiliconWat/khmer.siliconwat.dev/main/id/${id}.json`
        );
        const entry: Dictionary = await dictionary.json();
        this.definition.innerHTML = entry.definition;
        this.word.value = entry.word;
        this.addListeners();
        this.definition.style.opacity = "1";

        const history = JSON.parse(localStorage.getItem("history")!);
        history.push(entry);
        localStorage.setItem("history", JSON.stringify(history));
        this.back.disabled = false;
        this.back.classList.remove("hidden");
        
        this.heartbank.alertAdmin("dictionary/click/", this.word.value);
    }

    async renderWord(word: string) {
        this.definition.style.opacity = "0.2";
        if (word === "") {
            this.definition.textContent = strings.search2[LANGUAGE];
            setTimeout(() => this.word.focus(), 500);
        } else {
            try {
                const dictionary = await fetch(
                    `https://raw.githubusercontent.com/SiliconWat/khmer.siliconwat.dev/main/word/${encodeURIComponent(
                        word
                    )}.json`
                );
                const entry: Dictionary = await dictionary.json();
                this.definition.innerHTML = entry.definition;
                this.addListeners();
                localStorage.setItem("history", JSON.stringify([entry]));
            } catch {
                this.definition.textContent = strings.search4[LANGUAGE];
            } finally {
                this.word.value = word;
                this.back.disabled = true;
                this.back.classList.add("hidden");
            }
        }
        this.definition.style.opacity = "1";
        this.heartbank.alertAdmin("dictionary/submit/", word);
    }

    // TODO: add listener at parent level instead of onclick
    private addListeners() {
        this.definition
            .querySelectorAll("span[data-word]")
            .forEach((element) => {
                const span = element as HTMLSpanElement;
                span.onclick = () => this.renderId(span.dataset.word!);
            });
    }
}
